import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Card, FormActions, Pill, Tabs , fmtBRL} from "@atlas/ui/web";
import { ApiHttpError } from "@atlas/sdk";
import { atlas } from "../../lib/sdk";
import {
  ContratoMock as Contrato,
  MatriculaInfo,
  readActiveMatricula,
  hydrateMatriculas,
  STORAGE_KEY_ID,
  STORAGE_KEY_META,
} from "../../lib/matricula-data";
// buildSimplePdf/downloadPdf saíram — antes gerava um PDF fake; agora baixa
// o CCB real anexado pelo banco via atlas.servidor.baixarContratoCcb().


/** Estado agrupado da proposta pro servidor (view). */
type EstadoProposta = "em_analise" | "aprovada" | "aguardando_formalizacao" | "recusada" | "expirada" | "cancelada" | "liberada" | "desligada" | "falha_em_folha" | "aguardando_servidor";

function mapSituacao(situacao: string): EstadoProposta {
  const t = situacao.toLowerCase();
  // ATENCAO: ordem importa — checagens negativas primeiro pra evitar que
  // "Cancelado" caia em "aguard" (nunca cairia, mas defensivo).
  if (t.includes("cancel") || t.includes("estorn")) return "cancelada";
  if (t.includes("recus") || t.includes("reprov") || t.includes("rejeit") || t.includes("negad")) return "recusada";
  if (t.includes("suspens")) return "cancelada";
  if (t.includes("expir")) return "expirada";
  // Falha em folha: averbadora reportou falha, aguardando banco decidir.
  // Vai pro Historico (nao mais em andamento) — servidor ve o motivo.
  if (t.includes("falha em folha")) return "falha_em_folha";
  // "Em cobranca direta" = servidor foi desligado (F6). Contrato existe mas
  // saiu da folha; banco cobra direto. Deve aparecer com etapas completas
  // ate averbacao + marker vermelho na etapa final.
  if (t.includes("cobran")) return "desligada";
  if (t.includes("quitad")) return "liberada";
  if (t.includes("ativo") || t.includes("averb")) return "liberada";
  // Banco aprovou mas ainda nao averbou (situacao intermediaria "Aprovado")
  // — antes caia no fallback "em_analise" que iludia o servidor a achar que
  // ainda estava sob analise.
  if (t.includes("aprov")) return "aprovada";
  if (t.includes("formaliz")) return "aguardando_formalizacao";
  // "Aguardando Confirmacao do Servidor" — averbacao iniciada pelo banco
  // que precisa de senha do servidor pra virar Ativo.
  if (t.includes("aguard confirm")) return "aguardando_servidor";
  if (t.includes("aguard")) return "em_analise";
  return "em_analise";
}

/** Label do produto pro card do servidor. tipoContrato + tipoMargem vem
 *  do backend. Distingue os 4 casos:
 *   - REFIN                                      -> Portabilidade
 *   - ECONSIGNADO + tipoMargem=CARTAO_BENEFICIOS -> Cartao Beneficio
 *   - ECONSIGNADO (default)                      -> Cartao Consignado
 *   - EMPRESTIMO / undefined                     -> Emprestimo Consignado */
function mapProduto(tipoContrato: string | undefined, tipoMargem?: string, observacoes?: string): string {
  if (observacoes && /telemedicina/i.test(observacoes)) return "Plano Telemedicina";
  const t = (tipoContrato ?? "").toUpperCase();
  if (t === "REFIN") return "Portabilidade";
  if (t === "ECONSIGNADO") {
    return tipoMargem === "CARTAO_BENEFICIOS" ? "Cartão benefício" : "Cartão consignado";
  }
  return "Empréstimo consignado";
}

const ESTADO_LABEL: Record<EstadoProposta, string> = {
  em_analise: "Em análise pelo banco",
  aprovada: "Aprovada",
  aguardando_formalizacao: "Aguardando assinatura",
  recusada: "Recusada",
  expirada: "Expirada",
  cancelada: "Cancelada",
  liberada: "Averbada — virou contrato",
  desligada: "Encerrado — cobrança direta com o banco",
  falha_em_folha: "Falha em folha — aguardando decisão do banco",
  aguardando_servidor: "Aguarda sua confirmação",
};

/** Telemedicina nao passa pelo banco — o fluxo e servidor -> averbadora ->
 *  ADF. O pill precisa refletir isso pra bater com a timeline logo abaixo
 *  (que ja mostra "Averbadora avaliando" na etapa 2). */
function estadoLabelParaPill(estado: EstadoProposta, telemedicina: boolean): string {
  if (!telemedicina) return ESTADO_LABEL[estado];
  switch (estado) {
    case "em_analise": return "Em análise pela averbadora";
    case "aprovada": return "Aprovada pela averbadora";
    case "aguardando_formalizacao": return "Aguardando contrato";
    case "liberada": return "Plano ativo";
    case "desligada": return "Encerrado — cobrança direta";
    case "falha_em_folha": return "Falha em folha — aguardando averbadora";
    default: return ESTADO_LABEL[estado];
  }
}

/** Rotulo do produto no card do contrato. Usa tipoContrato + tipoMargem do
 *  backend pra distinguir os 4 casos:
 *   - REFIN                                      -> Portabilidade
 *   - ECONSIGNADO + tipoMargem=CARTAO_BENEFICIOS -> Cartao Beneficio
 *   - ECONSIGNADO (default)                      -> Cartao Consignado
 *   - EMPRESTIMO / default                       -> Emprestimo Consignado
 *  Fallback pra contratos antigos sem esses campos: olha substring no banco
 *  (compat com codigo antes do backend expor tipoContrato/tipoMargem). */
function produtoContratoLabel(c: { tipoContrato?: string; tipoMargem?: string; banco: string }): string {
  const t = (c.tipoContrato ?? "").toUpperCase();
  if (t === "REFIN") return "Portabilidade";
  if (t === "ECONSIGNADO") {
    return c.tipoMargem === "CARTAO_BENEFICIOS" ? "Cartão benefício" : "Cartão consignado";
  }
  if (t === "EMPRESTIMO") return "Empréstimo consignado";
  // Fallback dado antigo — nao tinha tipoContrato exposto no payload
  if (c.banco.toLowerCase().includes("refin")) return "Portabilidade";
  return "Empréstimo consignado";
}

function estadoPillVariant(e: EstadoProposta): "aceita" | "pendente" | "expirado" | "averbado" {
  if (e === "liberada") return "averbado";
  if (e === "recusada" || e === "expirada" || e === "cancelada" || e === "falha_em_folha" || e === "desligada") return "expirado";
  if (e === "aprovada" || e === "aguardando_formalizacao") return "aceita";
  return "pendente";
}

/** Uma proposta ainda "viva" (nao virou contrato averbado ainda). */
function ehEmAndamento(estado: EstadoProposta): boolean {
  return estado === "em_analise" || estado === "aprovada" || estado === "aguardando_formalizacao" || estado === "aguardando_servidor";
}

/** Proposta que terminou negativa — vai pro Historico. */
function ehHistoricoProposta(estado: EstadoProposta): boolean {
  return estado === "recusada" || estado === "expirada" || estado === "cancelada" || estado === "desligada" || estado === "falha_em_folha";
}

/** Converte "DD/MM/YYYY" (formato do backend) em "YYYY-MM-DD" pra comparar
 *  como string diretamente. Se o formato nao bate, devolve a string original
 *  (fallback: ordem indeterminada mas nao quebra). */
function sortKeyProposta(data: string): string {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(data);
  if (!m) return data;
  return `${m[3]}-${m[2]}-${m[1]}`;
}

export function ServidorContratos() {
  // Aba "Todos" foi removida a pedido do cliente. Ficou so Ativos (default)
  // e Historico. Historico inclui contratos quitados + propostas recusadas/
  // expiradas/canceladas.
  const [tab, setTab] = useState<"ativos" | "historico">("ativos");
  const [downloading, setDownloading] = useState<string | null>(null);
  const [info, setInfo] = useState<MatriculaInfo | null>(() => readActiveMatricula());
  const [averbacaoModal, setAverbacaoModal] = useState<{ adf: string; banco: string; valor: number; parcelas: number; parcela: number; taxaAm: number } | null>(null);
  const qc = useQueryClient();

  // Averbações INICIADAS PELO BANCO que estao pendentes de confirmacao com senha
  // do servidor (feature 4+6+7-bis, cliente 29/07/2026). Poll 10s.
  const averbacoesPendentesQ = useQuery({
    queryKey: ["servidor", "averbacoes-pendentes"],
    queryFn: () => atlas.servidor.averbacoesPendentes(),
    refetchInterval: 10_000,
  });

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY_META || e.key === STORAGE_KEY_ID) {
        setInfo(readActiveMatricula());
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // Rehidrata as matriculas do backend em intervalos regulares — o /me/matriculas
  // devolve `contratos` atualizado (ex.: contrato acabou de virar Ativo). Sem
  // isso o servidor teria que refresh a pagina pra ver contratos novos, ja que
  // o info sai do localStorage.
  useEffect(() => {
    // Primeira chamada imediata (garante fresh na entrada da tela) + poll 8s.
    void hydrateMatriculas();
    const iv = setInterval(() => { void hydrateMatriculas(); }, 8_000);
    return () => clearInterval(iv);
  }, []);

  // Propostas em andamento (o servidor solicitou, o banco ainda nao averbou).
  // Poll de 10s pra o servidor ver quando muda de "em_analise" pra "aprovada" etc.
  const propostasQ = useQuery({
    queryKey: ["servidor", "propostas", info?.matricula],
    queryFn: () => atlas.servidor.propostas(info?.matricula),
    enabled: !!info?.matricula,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
  });

  // Todas as propostas mapeadas — separa em andamento e historico.
  const propostasMapeadas = useMemo(() => {
    return (propostasQ.data?.propostas ?? []).map((p) => ({
      id: p.id,
      banco: p.banco,
      estado: mapSituacao(p.situacao),
      // tipoContrato do backend: EMPRESTIMO | REFIN | ECONSIGNADO.
      // Usado pra mostrar "Empréstimo / Portabilidade / Cartão" no card
      // (senao o servidor nao sabe pra qual produto e a proposta).
      produto: mapProduto(p.tipoContrato, p.tipoMargem, p.observacoes),
      observacoes: p.observacoes,
      valor: p.valor,
      parcelas: p.parcelas,
      parcela: p.parcela,
      taxaAm: p.taxaAm,
      data: p.data,
      // Timestamp ISO exato pro sort (com hora/min/seg). Se ausente (dado
      // antigo), cai no `data` (DD/MM/YYYY) — nesse caso propostas do mesmo
      // dia empatam mas ao menos dias diferentes ficam ordenados.
      criadoEmIso: p.criado_em_iso ?? null,
      // Vem do backend quando existir — usado pra rotular o produto no card.
      tipoContrato: p.tipoContrato,
      // Motivo da falha em folha (quando aplicavel) — mostrado no card do Historico.
      folhaMotivo: p.folhaMotivo,
      // Edicao do banco — quando presente, mostra o "antes / depois" no card
      // (fluxo de negociacao offline: banco liga, ajusta valores). Null se
      // nao editou.
      editadaPeloBanco: p.editadaPeloBanco ?? null,
    }));
  }, [propostasQ.data]);

  // Chave de sort: prefer ISO exato; fallback pra data DD/MM/YYYY invertido
  // pra YYYY-MM-DD. Usado tanto em "Em andamento" quanto no "Historico".
  const sortKey = (p: { criadoEmIso: string | null; data: string }): string => {
    if (p.criadoEmIso) return p.criadoEmIso;
    return sortKeyProposta(p.data);
  };

  const emAndamento = useMemo(
    () => propostasMapeadas
      .filter((p) => ehEmAndamento(p.estado))
      // Recentes no topo — cliente pediu explicitamente: quando uma proposta
      // nova chega, todas as outras descem 1 posicao; a que estava no topo
      // NAO vai pro fim, so desce uma linha.
      .sort((a, b) => sortKey(b).localeCompare(sortKey(a))),
    [propostasMapeadas],
  );
  const propostasHistorico = useMemo(
    () => propostasMapeadas
      .filter((p) => ehHistoricoProposta(p.estado))
      // Mesma regra do "Em andamento": recentes no topo.
      .sort((a, b) => sortKey(b).localeCompare(sortKey(a))),
    [propostasMapeadas],
  );

  const contratos: Contrato[] = info?.contratos ?? [];
  const filtered = contratos.filter((c) => {
    if (tab === "ativos") return c.status !== "Quitado";
    // historico
    return c.status === "Quitado";
  });

  async function baixarPdf(c: Contrato) {
    setDownloading(c.id);
    // Baixa o CCB REAL que o banco anexou (armazenado em R2). Antes o front
    // gerava um PDF fake via buildSimplePdf — cliente reclamou que nao era o
    // que o banco enviou. 404 = banco ainda nao anexou; qualquer outro erro
    // e' de rede/servidor.
    try {
      const blob = await atlas.servidor.baixarContratoCcb(c.id);
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `contrato-${c.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      const err = e as Error & { status?: number; reason?: string };
      // Mensagem por reason concreto que o backend retorna. Se nao vier reason,
      // cai no default — evita o falso positivo antigo de "banco nao anexou"
      // quando na verdade era outro problema (contrato nao encontrado no
      // isolate, arquivo sumiu do R2, etc).
      const alertMsg = (() => {
        if (err.status !== 404) return "Não foi possível baixar o contrato agora. Tente novamente em alguns instantes.";
        if (err.reason === "ccb_nao_anexado") return "O banco ainda não anexou o contrato assinado. Assim que ele enviar, o download fica disponível aqui.";
        if (err.reason === "contrato_nao_encontrado") return "Contrato não encontrado — pode estar sincronizando ainda. Aguarde alguns segundos e tente novamente.";
        if (err.reason === "arquivo_nao_encontrado_no_r2") return "O contrato foi anexado, mas o arquivo não está mais disponível. Peça ao banco pra reenviar.";
        if (err.reason === "contrato_nao_e_seu") return "Este contrato não pertence à sua matrícula.";
        // Fallback: mostra o reason cru pra ajudar no debug se aparecer alguma coisa nova.
        return err.reason ? `Não foi possível baixar o contrato (${err.reason}).` : "Não foi possível baixar o contrato. Tente novamente.";
      })();
      alert(alertMsg);
    } finally {
      setDownloading(null);
    }
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header>
        <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
          Meus contratos
        </span>
        <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Contratos e propostas</h1>
        <p style={{ color: "var(--text-muted)", marginTop: 6, fontSize: ".9rem" }}>
          {info ? <>Matrícula <b>{info.matricula}</b> ({info.prefeitura}). </> : null}
          Uma proposta vira contrato quando o banco confirma a averbação em folha.
        </p>
      </header>

      {/* Averbacoes iniciadas pelo banco aguardando confirmacao do servidor
          (cliente 29/07/2026). Bloco destacado — o servidor precisa aceitar
          com senha antes do contrato virar Ativo. */}
      {(averbacoesPendentesQ.data?.pendentes ?? []).length > 0 ? (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--gold-500)", textTransform: "uppercase" }}>
            Averbações aguardando sua confirmação
          </span>
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
            O banco iniciou uma operação sobre sua margem — <b>ela só entra em folha se você aceitar com sua senha</b>. Confira os termos antes.
          </p>
          {(averbacoesPendentesQ.data?.pendentes ?? []).map((p) => (
            <Card key={p.adf} style={{ borderColor: "var(--gold-500)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700 }}>{p.banco}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--gold-500)", padding: "2px 8px", borderRadius: 999, border: "1px solid var(--gold-500)", background: "color-mix(in srgb, var(--gold-500) 10%, transparent)" }}>
                      Aguarda sua senha
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                    ADF {p.adf} · {p.tipoMargem ?? p.tipoContrato}
                  </div>
                </div>
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 12, marginTop: 14, fontSize: 13,
              }}>
                <KV label="Valor" v={fmtBRL(p.valor)} accent />
                <KV label="Parcelas" v={`${p.parcelas}x de ${fmtBRL(p.parcela)}`} />
                <KV label="Taxa a.m." v={`${(p.taxaAm * 100).toFixed(2)}%`} />
              </div>
              {p.observacoes ? (
                <div style={{ marginTop: 10, fontSize: 12, color: "var(--text-muted)" }}>
                  Observações do banco: {p.observacoes}
                </div>
              ) : null}
              <div style={{ marginTop: 16, display: "flex", gap: 12, flexWrap: "wrap" }}>
                <Button variant="success" size="sm" onClick={() => setAverbacaoModal({ adf: p.adf, banco: p.banco, valor: p.valor, parcelas: p.parcelas, parcela: p.parcela, taxaAm: p.taxaAm })}>
                  Aceitar (com senha)
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setAverbacaoModal({ adf: p.adf, banco: p.banco, valor: p.valor, parcelas: p.parcelas, parcela: p.parcela, taxaAm: p.taxaAm })}>
                  Recusar
                </Button>
              </div>
            </Card>
          ))}
        </section>
      ) : null}

      {/* Propostas em andamento — o servidor ve o que solicitou virar contrato */}
      {emAndamento.length > 0 ? (
        <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Em andamento ({emAndamento.length})
          </span>
          <p style={{ fontSize: 12, color: "var(--text-muted)", margin: 0 }}>
            Propostas que você criou e ainda estão sendo analisadas pelo banco. Quando forem averbadas, aparecem abaixo como contrato.
          </p>
          {emAndamento.map((p) => {
            return (
            <Card key={p.id} style={{ borderColor: "var(--gold-500)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700 }}>{p.banco}</span>
                    <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--accent)", padding: "2px 8px", borderRadius: 999, border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)", background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}>
                      {p.produto}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                    Proposta {p.id} · criada em {p.data}
                  </div>
                </div>
                <Pill variant={estadoPillVariant(p.estado)}>{estadoLabelParaPill(p.estado, /telemedicina/i.test(p.observacoes ?? ""))}</Pill>
              </div>
              <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                gap: 12, marginTop: 14, fontSize: 13,
              }}>
                <KV label="Valor solicitado" v={fmtBRL(p.valor)} accent />
                <KV label="Parcelas" v={`${p.parcelas}x de ${fmtBRL(p.parcela)}`} />
                <KV label="Taxa a.m." v={`${(p.taxaAm * 100).toFixed(2)}%`} />
              </div>

              {/* Linha de progresso — em que etapa a proposta esta agora. */}
              <ProgressoProposta estado={p.estado} telemedicina={/telemedicina/i.test(p.observacoes ?? "")} />

              {/* Banco editou os valores da proposta (fluxo de negociacao
                  offline). Mostra "antes / depois" pra transparencia — servidor
                  ja concordou por telefone, aqui e' so o registro do que mudou. */}
              {p.editadaPeloBanco ? (
                <div style={{
                  marginTop: 14, padding: 14, borderRadius: 10,
                  border: "1px solid var(--gold-500)",
                  background: "color-mix(in srgb, var(--gold-500) 8%, transparent)",
                  display: "flex", flexDirection: "column", gap: 10,
                }}>
                  <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", color: "var(--gold-500)", textTransform: "uppercase" }}>
                    {p.banco} atualizou os valores
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
                    A proposta foi ajustada apos negociacao com o banco. Os valores mostrados acima ja sao os acordados.
                  </div>
                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                    gap: 10, fontSize: 13,
                  }}>
                    <KV label="Valor original" v={fmtBRL(p.editadaPeloBanco.valorOriginal)} />
                    <KV label="Parcelas originais" v={`${p.editadaPeloBanco.parcelasOriginal}x`} />
                    <KV label="Taxa original" v={`${(p.editadaPeloBanco.taxaAmOriginal * 100).toFixed(2)}%`} />
                  </div>
                  {p.editadaPeloBanco.motivo ? (
                    <div style={{ fontSize: 12, color: "var(--text-muted)" }}>
                      Motivo do banco: {p.editadaPeloBanco.motivo}
                    </div>
                  ) : null}
                </div>
              ) : null}
            </Card>
            );
          })}
        </section>
      ) : null}

      {/* Contratos ativos e historico (fluxo antigo) */}
      <section style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            {tab === "ativos" ? "Contratos averbados" : "Histórico"}
          </span>
          <Tabs
            variant="pills"
            activeKey={tab}
            onChange={(k) => setTab(k as typeof tab)}
            tabs={[
              { key: "ativos", label: "Ativos" },
              { key: "historico", label: "Histórico" },
            ]}
          />
        </div>

        {/* Historico: propostas recusadas/expiradas/canceladas aparecem primeiro,
            depois os contratos quitados. */}
        {tab === "historico" && propostasHistorico.length > 0 ? (
          <div style={{ display: "grid", gap: 12 }}>
            {propostasHistorico.map((p) => {
              return (
              <Card key={p.id} style={{ opacity: 0.85 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 700 }}>{p.banco}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--accent)", padding: "2px 8px", borderRadius: 999, border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)", background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}>
                        {p.produto}
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                      Proposta {p.id} · criada em {p.data}
                    </div>
                  </div>
                  <Pill variant={estadoPillVariant(p.estado)}>{estadoLabelParaPill(p.estado, /telemedicina/i.test(p.observacoes ?? ""))}</Pill>
                </div>
                <div style={{
                  display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                  gap: 12, marginTop: 14, fontSize: 13,
                }}>
                  <KV label="Valor solicitado" v={fmtBRL(p.valor)} />
                  <KV label="Parcelas" v={`${p.parcelas}x de ${fmtBRL(p.parcela)}`} />
                  <KV label="Taxa a.m." v={`${(p.taxaAm * 100).toFixed(2)}%`} />
                </div>
                {/* Motivo da falha em folha — mostrado destacado no Historico
                    quando averbadora reportou falha na ADF. */}
                {(p.estado === "falha_em_folha" || p.estado === "desligada") && p.folhaMotivo ? (
                  <div style={{
                    marginTop: 12, padding: "10px 12px", borderRadius: 8,
                    background: "color-mix(in srgb, var(--danger-500) 8%, transparent)",
                    border: "1px solid color-mix(in srgb, var(--danger-500) 30%, transparent)",
                    fontSize: 13, color: "var(--text)",
                  }}>
                    <b style={{ color: "var(--danger-500)" }}>Motivo:</b> {p.folhaMotivo}
                    {p.estado === "falha_em_folha" ? (
                      <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 4 }}>
                        Sua margem foi liberada. O banco vai decidir se reenvia, cancela ou cobra direto.
                      </div>
                    ) : null}
                  </div>
                ) : null}
                {/* Mesma linha de progresso — pinta em vermelho onde parou. */}
                <ProgressoProposta estado={p.estado} telemedicina={/telemedicina/i.test(p.observacoes ?? "")} />
              </Card>
              );
            })}
          </div>
        ) : null}

        {filtered.length === 0 && (tab === "ativos" || propostasHistorico.length === 0) ? (
          <Card>
            <div style={{ textAlign: "center", padding: "24px 12px" }}>
              <div style={{ fontSize: 36, opacity: 0.5 }}>📄</div>
              <h3 style={{ marginTop: 12, marginBottom: 6 }}>
                {tab === "ativos" ? "Sem contratos ativos" : "Sem histórico"}
              </h3>
              <p style={{ color: "var(--text-muted)", fontSize: ".9rem", maxWidth: 380, margin: "0 auto" }}>
                {tab === "ativos"
                  ? "Você não tem contratos ativos nesta matrícula no momento."
                  : "Ainda não há contratos quitados nem propostas recusadas/expiradas nesta matrícula."}
              </p>
            </div>
          </Card>
        ) : filtered.length === 0 ? null : (
          <div style={{ display: "grid", gap: 12 }}>
            {filtered.map((c) => {
              // Guard total=0 pra nao gerar NaN na largura da barra (contrato
              // sem total de parcelas definido ainda).
              const pct = c.total > 0 ? (c.parcelasPagas / c.total) * 100 : 0;
              const quitado = c.status === "Quitado";
              const produto = produtoContratoLabel(c);
              return (
                <Card key={c.id}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
                    <div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700 }}>{c.banco}</span>
                        {/* Rotulo do produto — mesmo estilo dos cards de proposta. */}
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--accent)", padding: "2px 8px", borderRadius: 999, border: "1px solid color-mix(in srgb, var(--accent) 40%, transparent)", background: "color-mix(in srgb, var(--accent) 10%, transparent)" }}>
                          {produto}
                        </span>
                      </div>
                      <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                        Contrato #{c.id}
                      </div>
                    </div>
                    <Pill variant={quitado ? "emdia" : c.status === "Averbado" ? "averbado" : "aceita"}>{c.status}</Pill>
                  </div>

                  <div style={{
                    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))",
                    gap: 12, marginTop: 16, fontSize: 13,
                  }}>
                    <KV label="Parcela" v={fmtBRL(c.parcela)} accent />
                    <KV label="Progresso" v={`${c.parcelasPagas}/${c.total}`} />
                    <KV label="Próxima" v={c.proximaParcela} />
                    <KV label="Taxa a.m." v={`${(c.taxaAm * 100).toFixed(2)}%`} />
                    <KV label="Valor financiado" v={fmtBRL(c.valorFinanciado)} />
                  </div>

                  <div style={{ marginTop: 12, height: 6, background: "var(--bg-elev-2)", borderRadius: 3, overflow: "hidden" }}>
                    <div style={{
                      height: "100%", width: `${pct}%`,
                      background: quitado
                        ? "var(--emerald-500)"
                        : "linear-gradient(90deg, var(--gold-500), var(--emerald-500))",
                    }} />
                  </div>

                  <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {/* So mostra "Baixar Contrato" se o banco anexou o CCB (anexoNome).
                        Sem contrato anexado, esconde o botao e avisa. Cliente 31/07/2026. */}
                    {c.anexoNome ? (
                      <Button size="sm" variant="ghost" onClick={() => baixarPdf(c)} disabled={downloading === c.id}>
                        {downloading === c.id ? "Baixando…" : "📄 Baixar Contrato"}
                      </Button>
                    ) : (
                      <span style={{ fontSize: ".82rem", color: "var(--text-muted)", alignSelf: "center" }}>
                        Contrato ainda não anexado pelo banco.
                      </span>
                    )}
                    {!quitado ? (
                      <span style={{ fontSize: ".82rem", color: "var(--text-muted)", alignSelf: "center" }}>
                        Para quitar: fale com o {c.banco}.
                      </span>
                    ) : null}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </section>

      {averbacaoModal ? (
        <AverbacaoPendenteModal
          adf={averbacaoModal.adf}
          banco={averbacaoModal.banco}
          valor={averbacaoModal.valor}
          parcelas={averbacaoModal.parcelas}
          parcela={averbacaoModal.parcela}
          taxaAm={averbacaoModal.taxaAm}
          onClose={() => setAverbacaoModal(null)}
          onFinish={() => {
            qc.invalidateQueries({ queryKey: ["servidor", "averbacoes-pendentes"] });
            qc.invalidateQueries({ queryKey: ["servidor", "propostas"] });
            setAverbacaoModal(null);
          }}
        />
      ) : null}
    </div>
  );
}

/** Modal de aceitar/recusar averbacao iniciada pelo banco — exige senha. */
function AverbacaoPendenteModal({
  adf, banco, valor, parcelas, parcela, taxaAm, onClose, onFinish,
}: {
  adf: string; banco: string; valor: number; parcelas: number; parcela: number; taxaAm: number;
  onClose: () => void; onFinish: () => void;
}) {
  const [modo, setModo] = useState<"aceitar" | "recusar">("aceitar");
  const [senha, setSenha] = useState("");
  const [motivo, setMotivo] = useState("");
  const [erro, setErro] = useState<string | null>(null);
  const mut = useMutation({
    mutationFn: () => modo === "aceitar"
      ? atlas.servidor.confirmarAverbacaoPendente(adf, senha)
      : atlas.servidor.rejeitarAverbacaoPendente(adf, senha, motivo.trim() || undefined),
    onSuccess: () => onFinish(),
    onError: (err) => setErro(err instanceof ApiHttpError ? err.message : err instanceof Error ? err.message : "Falha"),
  });
  return (
    <div style={modalBackdrop} onMouseDown={(e) => { if (e.target === e.currentTarget && !mut.isPending) onClose(); }}>
      <div style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Averbação iniciada pelo {banco}</h3>
        <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
          ADF <code style={{ fontFamily: "var(--font-mono)" }}>{adf}</code>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(120px, 1fr))", gap: 10, fontSize: 13, padding: 12, background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 8 }}>
          <KV label="Valor" v={fmtBRL(valor)} accent />
          <KV label="Parcelas" v={`${parcelas}x de ${fmtBRL(parcela)}`} />
          <KV label="Taxa a.m." v={`${(taxaAm * 100).toFixed(2)}%`} />
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant={modo === "aceitar" ? "primary" : "ghost"} size="sm" onClick={() => setModo("aceitar")}>Aceitar</Button>
          <Button variant={modo === "recusar" ? "primary" : "ghost"} size="sm" onClick={() => setModo("recusar")}>Recusar</Button>
        </div>
        <div>
          <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Sua senha</label>
          <input
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoFocus
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--bg-elev)", color: "var(--text)", fontSize: 14, boxSizing: "border-box" }}
          />
        </div>
        {modo === "recusar" ? (
          <div>
            <label style={{ fontSize: 12, fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase", display: "block", marginBottom: 6 }}>Motivo (opcional)</label>
            <textarea value={motivo} onChange={(e) => setMotivo(e.target.value)} rows={2} maxLength={300}
              style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid var(--border-strong)", background: "var(--bg-elev)", color: "var(--text)", fontSize: 13, boxSizing: "border-box", fontFamily: "inherit", resize: "vertical" }}
            />
          </div>
        ) : null}
        {erro ? (
          <div style={{ padding: "8px 12px", borderRadius: 8, background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", border: "1px solid var(--danger-500)", fontSize: 13, color: "var(--danger-500)" }}>
            {erro}
          </div>
        ) : null}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose} disabled={mut.isPending}>Cancelar</Button>
          <Button type="button" onClick={() => { setErro(null); mut.mutate(); }} disabled={mut.isPending || !senha}>
            {mut.isPending ? "Enviando..." : modo === "aceitar" ? "Aceitar averbação" : "Recusar averbação"}
          </Button>
        </FormActions>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 520, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
};

function KV({ label, v, accent }: { label: string; v: string; accent?: boolean }) {
  return (
    <div>
      <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-dim)", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ marginTop: 4, color: accent ? "var(--accent)" : "var(--text)", fontWeight: accent ? 700 : 500 }}>
        {v}
      </div>
    </div>
  );
}

/** Etapas do ciclo de vida da proposta pro servidor acompanhar (5 estagios).
 *  A etapa 2 tem label NEUTRO ("Analise pelo banco") — so vira "Recusada pelo
 *  banco" via labelEtapa2 quando a proposta for de fato recusada. Antes o
 *  label BASE dizia "Recusada" mesmo em propostas em analise, o que confundia
 *  o servidor (parecia que ja tinha sido recusada). */
const ETAPAS_BASE = [
  "Proposta enviada",
  "Análise pelo banco",
  "Aprovado pelo banco",
  "Aguardando averbação",
  "Averbação concluída",
] as const;

// Etapas customizadas pra plano de telemedicina — fluxo passa pela averbadora,
// nao por banco. Cinco etapas mapeadas 1:1 no mesmo eixo de 0..4.
const ETAPAS_TELEMEDICINA = [
  "Solicitação enviada",
  "Averbadora avaliando",
  "Contrato anexado",
  "Plano aprovado",
  "ADF em folha",
] as const;

/** Retorna { atual, falha, labelEtapa2? }.
 *  - `atual`: indice (0..4) da etapa em curso.
 *  - `falha`: quando true, a etapa atual vira vermelha e as demais ficam cinza.
 *  - `labelEtapa2`: sobrescreve o label da etapa 2 quando o estado terminou negativo. */
function estadoParaEtapa(estado: EstadoProposta): { atual: number; falha: boolean; labelEtapaAtual?: string } {
  if (estado === "em_analise") return { atual: 1, falha: false };
  if (estado === "aprovada" || estado === "aguardando_formalizacao") return { atual: 2, falha: false };
  if (estado === "liberada") return { atual: 4, falha: false };
  if (estado === "recusada") return { atual: 1, falha: true, labelEtapaAtual: "Recusada pelo banco" };
  if (estado === "expirada") return { atual: 1, falha: true, labelEtapaAtual: "Expirada" };
  if (estado === "cancelada") return { atual: 1, falha: true, labelEtapaAtual: "Cancelada" };
  // Desligado: passou por todas as etapas (foi averbado) mas o servidor saiu
  // da prefeitura. Mostra etapa final vermelha com label especifico.
  if (estado === "desligada") return { atual: 4, falha: true, labelEtapaAtual: "Encerrado por desligamento" };
  // Falha em folha: averbadora tentou aplicar mas rejeitou; aguardando banco decidir.
  // Chegou ate a etapa 3 (ADF averbadora) mas falhou nela.
  if (estado === "falha_em_folha") return { atual: 3, falha: true, labelEtapaAtual: "Falha — banco vai decidir" };
  return { atual: 0, falha: false };
}

function ProgressoProposta({ estado, telemedicina }: { estado: EstadoProposta; telemedicina?: boolean }) {
  const { atual, falha, labelEtapaAtual } = estadoParaEtapa(estado);
  const labels = [...(telemedicina ? ETAPAS_TELEMEDICINA : ETAPAS_BASE)] as string[];
  // Substitui o label da etapa "atual" quando ha customizacao (falhas + finais).
  if (labelEtapaAtual) labels[atual] = labelEtapaAtual;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 4 }}>
        {labels.map((nome, i) => {
          const feita = i < atual;
          const ativa = i === atual;
          // Se a proposta falhou, a etapa "atual" (ponto de parada) vira vermelha.
          const corAtiva = falha && ativa ? "var(--danger-500)" : "var(--emerald-500)";
          const cor = feita || ativa ? corAtiva : "var(--border-strong)";
          const corTexto = feita || ativa ? corAtiva : "var(--text-dim)";
          return (
            <div key={nome + i} style={{ display: "flex", alignItems: "flex-start", flex: i < labels.length - 1 ? 1 : "0 0 auto", gap: 4, minWidth: 0 }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, gap: 4, minWidth: 0 }}>
                <div
                  style={{
                    width: ativa ? 14 : 10,
                    height: ativa ? 14 : 10,
                    borderRadius: "50%",
                    background: feita || ativa ? cor : "transparent",
                    border: `2px solid ${cor}`,
                    boxShadow: ativa ? `0 0 0 4px color-mix(in srgb, ${cor} 20%, transparent)` : "none",
                    transition: "all .2s ease",
                  }}
                  aria-hidden="true"
                />
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: ativa ? 700 : 600,
                    color: corTexto,
                    textAlign: "center",
                    lineHeight: 1.2,
                    maxWidth: 92,
                  }}
                >
                  {nome}
                </span>
              </div>
              {i < labels.length - 1 ? (
                <div style={{
                  flex: 1,
                  height: 2,
                  background: i < atual ? "var(--emerald-500)" : "var(--border-strong)",
                  marginTop: 6, // alinha com o meio do circulo (o label vem abaixo)
                }} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
